// Iter-194u — CEO/Gestionnaire Dashboard widget · Invoice payment status counts
//
// 4 KPI cards: Active / Approaching deadline (at least 1 reminder fired) /
// Overdue (delay passed, unpaid) / Paid this period. Clicking a non-zero
// bucket navigates to /invoices?payment_status=<bucket> which the Invoices
// page filters on.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Receipt, TrendingUp, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

export default function InvoicePaymentStatusBlock() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/invoices/payment-status-counts");
      setData(r.data || {});
    } catch (_e) {
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const go = (status) => {
    navigate(`/invoices?payment_status=${status}`);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="invoice-payment-status-block">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Receipt size={18} className="text-zinc-700" />
          <div>
            <div className="font-heading font-bold text-base text-zinc-900">Factures clients — statut de règlement</div>
            <div className="text-[11px] text-zinc-500 font-mono">
              Cliquez sur un bloc pour ouvrir la liste filtrée
            </div>
          </div>
        </div>
        {loading && <Loader2 size={14} className="animate-spin text-zinc-400" />}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          testid="invoice-status-active"
          icon={<CheckCircle2 size={14} />}
          label="En cours"
          value={data?.active ?? "—"}
          tone="neutral"
          onClick={() => data?.active && go("active")}
          hint="Envoyées, pas encore de relance"
        />
        <KpiCard
          testid="invoice-status-approaching"
          icon={<TrendingUp size={14} />}
          label="Délai approche"
          value={data?.approaching ?? "—"}
          tone={(data?.approaching || 0) > 0 ? "warn" : "neutral"}
          onClick={() => data?.approaching && go("approaching")}
          hint="Au moins 1 relance envoyée"
        />
        <KpiCard
          testid="invoice-status-overdue"
          icon={<AlertTriangle size={14} />}
          label="En retard"
          value={data?.overdue ?? "—"}
          tone={(data?.overdue || 0) > 0 ? "danger" : "ok"}
          onClick={() => data?.overdue && go("overdue")}
          hint="Délai contractuel dépassé"
        />
        <KpiCard
          testid="invoice-status-paid"
          icon={<CheckCircle2 size={14} />}
          label="Payées"
          value={data?.paid ?? "—"}
          tone="ok"
          onClick={() => data?.paid && go("paid")}
          hint="Réglées intégralement"
        />
      </div>

      {data && data.total_outstanding > 0 && (
        <div className="mt-3 text-[11px] text-zinc-500 font-mono">
          Encours total : <b>{data.total_outstanding}</b> facture(s) non réglée(s)
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, tone, icon, onClick, hint, testid }) {
  const clickable = !!onClick && typeof value === "number" && value > 0;
  const toneCls = tone === "danger"
    ? "bg-red-50 border-red-200 text-red-900"
    : tone === "warn"
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "ok"
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-zinc-50 border-zinc-200 text-zinc-900";
  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      data-testid={testid}
      className={`text-left border rounded-md px-3 py-2.5 ${toneCls} ${clickable ? "hover:shadow-md hover:scale-[1.02] cursor-pointer transition-all" : "cursor-default opacity-90"}`}
    >
      <div className="text-[9px] uppercase tracking-widest font-mono opacity-70 flex items-center gap-1 mb-1">
        {icon}
        {label}
      </div>
      <div className="font-mono text-2xl font-semibold leading-tight">{value}</div>
      {hint && <div className="text-[10px] opacity-70 mt-1 truncate">{hint}</div>}
    </button>
  );
}
